import { ENABLED_COUNTRIES } from '@/lib/enabled_countries'
import { GetTurnstileToken } from '@/src/lib/cloudflareTurnstile'
import { useSendOtpVerifyPhone } from '@/src/mutations/account/otp-mutation'
import { useVerifyPhoneMutation } from '@/src/mutations/account/verify-phone'
import { useUser } from '@/src/store/user-store'
import {
  Box,
  Text,
  Flex,
  Button,
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalFooter,
  ModalBody,
  ModalCloseButton,
  Input,
  FormLabel,
  Image,
  InputGroup,
  InputLeftAddon,
  Select,
} from '@chakra-ui/react'
import React, { useEffect, useRef, useState } from 'react'

import OtpInput from 'react-otp-input'
import { toast } from 'sonner'

const labelStyles = {
  fontSize: {
    base: 'sm',
    md: 'md',
  },
  mt: {
    base: '1',
    md: '1',
  },
  fontWeight: '500',
  marginBottom: '5px',
  color: '#525252',
}

const inputStyles = {
  size: {
    base: 'md',
  },
  rounded: 'md',
  border: '1px solid #E5E5E5',
  px: {
    base: '3',
    md: '4',
  },
  fontSize: {
    base: 'sm',
    md: 'md',
  },
}

export default function VerifyPhoneEmail({
  isOpen,
  onClose,
  onVerifyClose,
}: {
  isOpen: boolean
  onClose: () => void
  onVerifyClose: () => void
}) {
  const [otp, setOtp] = useState('')
  const [step, setStep] = useState(0)
  const { updateVerificationStatus, updatePhone, user } = useUser()
  const verifyPhone = useVerifyPhoneMutation()
  const sendOtp = useSendOtpVerifyPhone()
  const [countryCode, setCountryCode] = useState(user?.countryCode)
  const [phoneNumber, setPhoneNumber] = useState(user?.phoneNumber)
  const [resendAllowed, setResendAllowed] = useState(true)
  const timerRef = useRef<NodeJS.Timeout>()
  const [isOtpSent, setIsOtpSent] = useState(false)
  const [otpError, setOtpError] = useState('')
  const [phoneError, setPhoneError] = useState('')
  const [isFetchingPhoneOtpToken, setIsFetchingPhoneOtpToken] = useState(false)
  const dialCodeLength = (countryCode || '').replace(/\D/g, '').length
  const phoneNumberLength = Math.min(10, 12 - dialCodeLength)
  const isPhoneNumberValid = new RegExp(`^[0-9]{${phoneNumberLength}}$`).test(
    phoneNumber || ''
  )

  const onSubmit = () => {
    if (!otp) return
    if (otp.length != 4) {
      setOtpError('Please enter a valid verification code')
      return
    }
    verifyPhone.mutate(
      { otp: otp },
      {
        onSuccess: () => {
          setStep(1)
          toast.success('Phone number verified successfully!')
          updateVerificationStatus(true)
          updatePhone(phoneNumber || '', countryCode || '')
        },
        onError: (error) => {
          setOtpError(error.message)
        },
      }
    )
  }

  const sendOtpHandle = async () => {
    if (!resendAllowed) {
      return
    }

    if (!phoneNumber) {
      setPhoneError('Please enter a phone number')
      toast.error('Please enter a phone number')
      return
    }
    if (!countryCode) {
      toast.error('Please select a country code')
      return
    }

    if (!isPhoneNumberValid) {
      const message = `Phone number must be ${phoneNumberLength} digits`
      setPhoneError(message)
      toast.error(message)
      return
    }

    setPhoneError('')
    setOtpError('')
    setOtp('')

    setIsFetchingPhoneOtpToken(true)
    const cfToken = await GetTurnstileToken('cft-otpGenerateforPhone')
    // console.log('Breakdown phone email Turnstile token:', cfToken)

    if (!cfToken) {
      setIsFetchingPhoneOtpToken(false)
      toast.error('Unable to generate Turnstile token.')
      return
    }

    sendOtp.mutate(
      {
        countryCode: countryCode || '',
        phoneNumber: phoneNumber || '',
        cfToken: cfToken || '',
      },
      {
        onSuccess: () => {
          setIsOtpSent(true)
          setResendAllowed(false)
          toast.success('Verification code sent successfully!')
          timerRef.current = setTimeout(() => {
            setResendAllowed(true)
          }, 30000)
        },
        onError: (error) => {
          toast.error(error.message)
          setIsOtpSent(false)
        },
        onSettled: () => {
          setIsFetchingPhoneOtpToken(false)
        },
      }
    )
  }

  useEffect(() => {
    return () => {
      clearTimeout(timerRef.current)
    }
  }, [])

  useEffect(() => {
    setPhoneNumber(user?.phoneNumber)
    setCountryCode(user?.countryCode)
  }, [user])

  return (
    <Modal
      isOpen={isOpen}
      onClose={() => {
        onClose()
      }}
      size={'md'}
      isCentered
    >
      <ModalOverlay />
      <ModalContent>
        <ModalHeader color="#3E3E3E" opacity={0.7}>
          {step == 0 ? 'Verify Phone Number' : 'Verification complete'}
        </ModalHeader>
        <ModalCloseButton mt={2} />
        <hr />
        {step == 0 ? (
          <>
            <ModalBody mt={2}>
              <Text
                color={'#3a3a3a'}
                fontSize={16}
                fontWeight={600}
                opacity={0.7}
                mb={4}
              >
                Please verify your phone number to complete the redemption
                process.
              </Text>

              <FormLabel mb={2} htmlFor="number" {...labelStyles}>
                Phone number *
              </FormLabel>
              <InputGroup size="md">
                <InputLeftAddon
                  border={'1px solid #E5E5E5'}
                  minW={'60px'}
                  fontSize={{
                    base: 'sm',
                    md: 'md',
                  }}
                  px={0}
                >
                  <Select
                    fontSize={{
                      base: 'sm',
                      md: 'md',
                    }}
                    id="countryCode"
                    name="countryCode"
                    size={{
                      base: 'md',
                    }}
                    border={'none'}
                    _focusVisible={{
                      border: 'none',
                      boxShadow: 'none',
                    }}
                    value={countryCode}
                    onChange={(e) => {
                      setCountryCode(e.target.value)
                    }}
                  >
                    <option value={user?.countryCode} key={user?.countryCode}>
                      +{user?.countryCode}
                    </option>
                  </Select>
                </InputLeftAddon>
                <Input
                  {...inputStyles}
                  placeholder={'Enter phone number'}
                  type="tel"
                  inputMode="numeric"
                  maxLength={phoneNumberLength}
                  id="phone"
                  name="phone"
                  mb={2}
                  value={phoneNumber}
                  onChange={(e) => {
                    setPhoneNumber(e.target.value.replace(/\D/g, ''))
                    setPhoneError('')
                  }}
                />
              </InputGroup>
              {phoneError && (
                <Text color={'lyk-red'} fontSize={14} fontWeight={600} mb={2}>
                  {phoneError}
                </Text>
              )}
              <Button
                isDisabled={!isPhoneNumberValid || resendAllowed === false}
                paddingX={20}
                color="white"
                _hover={{ opacity: 0.7 }}
                _disabled={{ bg: '#eaeaea' }}
                mt={2}
                bg="lyk-green"
                size={['sm', 'sm', 'sm']}
                fontWeight={500}
                onClick={() => {
                  sendOtpHandle()
                }}
                isLoading={sendOtp.isPending || isFetchingPhoneOtpToken}
              >
                Send Verification Code
              </Button>

              {!resendAllowed && (
                <Text
                  fontSize={{ base: 'sm', md: 'sm' }}
                  color={'lyk-black'}
                  mt={2}
                  mb={2}
                >
                  Resend Verification Code in 30 seconds
                </Text>
              )}

              <Box
                opacity={isOtpSent ? 1 : 0.5}
                mt={4}
                hidden={!isOtpSent}
                pointerEvents={isOtpSent ? 'all' : 'none'}
              >
                <Text
                  color={'#3e3e3e'}
                  fontWeight={600}
                  fontSize={24}
                  opacity={0.8}
                  mb={0}
                >
                  Enter verification code
                </Text>
                <Text
                  color={'#3e3e3e'}
                  fontWeight={600}
                  fontSize={14}
                  opacity={0.6}
                  mb={3}
                >
                  We have sent the code to your Phone Number
                </Text>

                <OtpInput
                  value={otp}
                  onChange={setOtp}
                  numInputs={4}
                  inputType="number"
                  renderSeparator={<span>&nbsp;&nbsp;&nbsp;</span>}
                  inputStyle={{
                    width: '3rem',
                    height: '3rem',
                    fontSize: '1.5rem',
                    borderRadius: '0.25rem',
                    border: '1px solid #6699ee',
                    padding: '0.5em',
                  }}
                  renderInput={(props) => <input {...props} />}
                />

                {otpError && (
                  <Text mt={4} color={'lyk-red'} fontSize={14} fontWeight={600}>
                    {otpError}
                  </Text>
                )}
              </Box>
            </ModalBody>
            <hr className="mt-10" />
            <ModalFooter>
              <Flex gap={4} mb={0}>
                <Button
                  px={10}
                  onClick={onSubmit}
                  _hover={{ opacity: 0.5 }}
                  size={['sm', 'sm', 'md']}
                  fontWeight={500}
                  bg={'lyk-green'}
                  isLoading={verifyPhone.isPending}
                  color={'white'}
                  w={24}
                  isDisabled={
                    !otp || !phoneNumber || !isOtpSent || otp.length != 4
                  }
                >
                  Verify
                </Button>
              </Flex>
            </ModalFooter>
          </>
        ) : (
          <ModalBody py={5}>
            <Box mb={5} mt={2} display={'flex'} justifyContent={'center'}>
              <Image
                boxSize="75px"
                src={'/rewards/breakdown/greentick.svg'}
                alt="wallet"
              />
            </Box>
            <Text
              mb={4}
              textAlign={'center'}
              fontWeight={600}
              fontSize={24}
              opacity={0.7}
            >
              Phone number verified!
            </Text>

            <Text fontWeight={400} fontSize={16} textAlign={'center'} mb={6}>
              Now you just need to verify your ID and Beneficiary details to
              start redeeming!
            </Text>

            <Flex justifyContent={'center'} gap={4} mb={4}>
              <Button
                px={10}
                bg="lyk-green"
                color="white"
                _hover={{ opacity: 0.7 }}
                size={['sm', 'md', 'md']}
                fontWeight={500}
                onClick={onVerifyClose}
              >
                Proceed
              </Button>
            </Flex>
          </ModalBody>
        )}
      </ModalContent>
    </Modal>
  )
}
